import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  IconButton,
  Tooltip,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  InputAdornment,
  ListSubheader,
  SelectChangeEvent,
  Pagination,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DownloadIcon from '@mui/icons-material/Download';
import CloseIcon from '@mui/icons-material/Close';
import { format, addDays, subDays, differenceInDays } from 'date-fns';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { Assignment, Person, Special } from '../types';
import config from '../config';

const LAST_SELECTED_DATE_KEY = 'lastSelectedAssignmentDate';
const ITEMS_PER_PAGE = 20;

const MONDAY_FRIDAY_VAROS = ['First Ginan / Qasida', 'Farman Before 4am', 'Baitul Khayal Tasbih', 'Bandagi Ginan'];

const OTHER_DAYS_VAROS = ['First Ginan / Qasida', 'Farman Before 4am', 'Bandagi Ginan'];

const BK_SATADA_VAROS = [
  'First Ginan / Qasida',
  'Ruhani Visaal',
  'Farman Before 4am',
  'Satada Tasbih',
  'Bandagi Ginan',
];

const getAssignments = (assignments: any[], referenceDate: Date) => {
  const pastAssignments = assignments
    .filter((a) => new Date(a.startDate) <= referenceDate)
    .sort((a, b) => new Date(b.startDate).getTime() - new Date(a.startDate).getTime());

  const upcomingAssignments = assignments
    .filter((a) => new Date(a.startDate) > referenceDate)
    .sort((a, b) => new Date(a.startDate).getTime() - new Date(b.startDate).getTime());

  return {
    lastAssignment: pastAssignments[0],
    nextAssignment: upcomingAssignments[0],
  };
};

const getRelativeDays = (date: string, referenceDate: Date) => {
  const days = differenceInDays(new Date(date), referenceDate);
  if (days < 0) {
    return `(${Math.abs(days)} days ago)`;
  } else if (days > 0) {
    return `(in ${days} days)`;
  } else {
    return '(today)';
  }
};

const AssignmentManager = (): JSX.Element => {
  // Get the last selected date from localStorage or default to today
  const getInitialDate = () => {
    const savedDate = localStorage.getItem(LAST_SELECTED_DATE_KEY);
    if (savedDate) {
      // Parse as local midnight so we don't get off-by-one in timezones behind UTC
      // (parseISO('yyyy-MM-dd') treats date-only as UTC midnight)
      const d = new Date(savedDate + 'T00:00:00');
      if (!isNaN(d.getTime())) {
        return d;
      }
    }
    return new Date();
  };

  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [people, setPeople] = useState<Person[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [selectedTask, setSelectedTask] = useState('');
  const [selectedTaskId, setSelectedTaskId] = useState<number | null>(null);
  const [selectedPerson, setSelectedPerson] = useState<number | ''>('');
  const [editingAssignment, setEditingAssignment] = useState<Assignment | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date>(getInitialDate());
  const [allTasks, setAllTasks] = useState<
    Array<{ id: number; name: string; description?: string }>
  >([]);
  const [isCustomTask, setIsCustomTask] = useState(false);
  const [customTaskName, setCustomTaskName] = useState('');
  const [searchTask, setSearchTask] = useState('');
  const [searchName, setSearchName] = useState('');
  const [searchSkill, setSearchSkill] = useState('');
  const [filteredTasks, setFilteredTasks] = useState<
    Array<{ id: number; name: string; description?: string }>
  >([]);
  const [filteredPeople, setFilteredPeople] = useState<Person[]>([]);

  // New person dialog state
  const [openNewPersonDialog, setOpenNewPersonDialog] = useState(false);
  const [newFirstName, setNewFirstName] = useState('');
  const [newLastName, setNewLastName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newRanking, setNewRanking] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [selectedSkills, setSelectedSkills] = useState<number[]>([]);

  const [specialForDate, setSpecialForDate] = useState<Special | null>(null);
  const [specialDialogOpen, setSpecialDialogOpen] = useState(false);
  const [specialNameInput, setSpecialNameInput] = useState('');

  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [assignmentToDelete, setAssignmentToDelete] = useState<Assignment | null>(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  useEffect(() => {
    fetchPeople();
    fetchAllTasks();
  }, []);

  useEffect(() => {
    fetchAssignments();
    fetchSpecialForDate();
  }, [selectedDate]);

  const fetchSpecialForDate = async () => {
    try {
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      const response = await axios.get<Special[]>(
        `${config.api.baseUrl}/specials`,
        {
          params: { date: formattedDate },
          withCredentials: true,
        },
      );
      setSpecialForDate(response.data[0] ?? null);
    } catch (error) {
      console.error('Error fetching special for date:', error);
      setSpecialForDate(null);
    }
  };

  const fetchAllTasks = async () => {
    try {
      const response = await axios.get(`${config.api.baseUrl}/tasks`, {
        withCredentials: true,
      });
      console.log('Fetched tasks:', response.data);
      setAllTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    }
  };

  const fetchAssignments = async () => {
    try {
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');

      console.log('Fetching assignments for date:', {
        selectedDate: selectedDate.toLocaleString(),
        formattedDate,
        localTime: new Date().toLocaleString(),
      });

      const response = await axios.get(`${config.api.baseUrl}/assignments`, {
        params: {
          date: formattedDate,
        },
        withCredentials: true,
      });

      console.log('Received assignments:', response.data);
      setAssignments(response.data);
    } catch (error) {
      console.error('Error fetching assignments:', error);
    }
  };

  const fetchPeople = async () => {
    try {
      const response = await axios.get(`${config.api.baseUrl}/people`, {
        withCredentials: true,
      });
      setPeople(response.data);
    } catch (error) {
      console.error('Error fetching people:', error);
    }
  };

  const handleOpenDialog = (taskName: string, assignment?: Assignment) => {
    setSelectedTask(taskName);
    // Clear skill search box but set name search if editing
    setSearchSkill('');

    if (assignment) {
      setEditingAssignment(assignment);
      setSelectedPerson(assignment.personId);
      setSelectedTaskId(assignment.varosId);
      // Pre-fill the name search with current assignee's name
      const currentPerson = people.find((p) => p.id === assignment.personId);
      setSearchName(currentPerson?.name || '');
    } else {
      setEditingAssignment(null);
      setSelectedPerson('');
      setSearchName('');
      const task = allTasks.find((t) => t.name === taskName);
      setSelectedTaskId(task?.id || null);
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setSelectedTask('');
    setSelectedPerson('');
    setSelectedTaskId(null);
    setEditingAssignment(null);
  };

  const handleTaskChange = (event: any) => {
    const taskName = event.target.value;
    setSelectedTask(taskName);
    const task = allTasks.find((t) => t.name === taskName);
    setSelectedTaskId(task?.id || null);
  };

  const handleAssign = async () => {
    if (!selectedPerson || !selectedTaskId) {
      console.error('Missing required fields:', { selectedPerson, selectedTaskId });
      return;
    }

    try {
      const assignmentData = {
        personId: Number(selectedPerson),
        varosId: Number(selectedTaskId),
        startDate: format(selectedDate, 'yyyy-MM-dd'),
      };

      console.log('Assignment data:', {
        ...assignmentData,
        isEditing: !!editingAssignment,
        editingId: editingAssignment?.id,
      });

      if (editingAssignment) {
        console.log('Updating assignment:', editingAssignment.id);
        const response = await axios.put(
          `${config.api.baseUrl}/assignments/${editingAssignment.id}`,
          assignmentData,
          {
            withCredentials: true,
          }
        );
        console.log('Update response:', response.data);
      } else {
        console.log('Creating new assignment');
        const response = await axios.post(`${config.api.baseUrl}/assignments`, assignmentData, {
          withCredentials: true,
        });
        console.log('Create response:', response.data);
      }

      await fetchAssignments();
      handleCloseDialog();
    } catch (error: any) {
      console.error('Error in handleAssign:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        data: {
          personId: selectedPerson,
          varosId: selectedTaskId,
          startDate: format(selectedDate, 'yyyy-MM-dd'),
          editingId: editingAssignment?.id,
        },
      });
    }
  };

  const handleSaveSpecialForDate = async () => {
    const trimmedName = specialNameInput.trim();
    if (!trimmedName) return;

    try {
      const formattedDate = format(selectedDate, 'yyyy-MM-dd');
      const payload = {
        name: trimmedName,
        date: `${formattedDate}T00:00:00`,
      };

      if (specialForDate) {
        await axios.put(
          `${config.api.baseUrl}/specials/${specialForDate.id}`,
          payload,
          { withCredentials: true },
        );
      } else {
        await axios.post(
          `${config.api.baseUrl}/specials`,
          payload,
          { withCredentials: true },
        );
      }

      await fetchSpecialForDate();
      setSpecialDialogOpen(false);
    } catch (error) {
      console.error('Error saving special for date:', error);
    }
  };

  const handleRemoveSpecialForDate = async () => {
    if (!specialForDate) return;
    try {
      await axios.delete(
        `${config.api.baseUrl}/specials/${specialForDate.id}`,
        { withCredentials: true },
      );
      await fetchSpecialForDate();
      setSpecialDialogOpen(false);
    } catch (error) {
      console.error('Error removing special for date:', error);
    }
  };

  const today = format(new Date(), 'MMMM d, yyyy');

  // Function to determine which assignments to show (template varos for this date).
  // - BK Satada special day → BK_SATADA_VAROS
  // - Other special day (name set but not BK Satada) → no template (manual only)
  // - No special → Mon/Fri → MONDAY_FRIDAY_VAROS, else OTHER_DAYS_VAROS
  const getStandardAssignments = () => {
    const isBKSatada =
      specialForDate?.name?.trim().toLowerCase() === 'bk satada';
    if (isBKSatada) {
      return BK_SATADA_VAROS;
    }
    // Special day other than BK Satada: no default template; user adds via Create Varo Assignment
    if (specialForDate?.name?.trim()) {
      return [];
    }

    const dayOfWeek = selectedDate.getDay(); // 0 = Sunday, 1 = Monday, ..., 6 = Saturday
    if (dayOfWeek === 1 || dayOfWeek === 5) {
      return MONDAY_FRIDAY_VAROS;
    }
    return OTHER_DAYS_VAROS;
  };

  const standardVarosTemplate = getStandardAssignments();
  const isCustomSpecialDay = !!(specialForDate?.name?.trim() && specialForDate.name.trim().toLowerCase() !== 'bk satada');

  const standardAssignments = assignments.filter(
    (a) =>
      standardVarosTemplate.includes(a.varos.name) &&
      format(new Date(a.startDate), 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd'),
  );

  const nonStandardAssignments = assignments
    .filter(
      (a) =>
        !standardVarosTemplate.includes(a.varos.name) &&
        format(new Date(a.startDate), 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd'),
    )
    .sort((a, b) => {
      const aTime = new Date(a.createdAt ?? a.startDate).getTime();
      const bTime = new Date(b.createdAt ?? b.startDate).getTime();
      return aTime - bTime; // oldest (earliest created) first
    });

  console.log('Filtered standard assignments:', {
    total: assignments.length,
    standard: standardAssignments.length,
    standardTasks: standardAssignments.map((a) => ({
      task: a.varos.name,
      person: a.person.name,
      date: a.startDate,
      special: a.special,
    })),
  });

  console.log('Filtered non-standard assignments:', {
    total: assignments.length,
    nonStandard: nonStandardAssignments.length,
    nonStandardTasks: nonStandardAssignments.map((a) => ({
      task: a.varos.name,
      person: a.person.name,
      date: a.startDate,
      special: a.special,
    })),
  });

  const handlePreviousDay = () => {
    const newDate = subDays(selectedDate, 1);
    setSelectedDate(newDate);
    localStorage.setItem(LAST_SELECTED_DATE_KEY, format(newDate, 'yyyy-MM-dd'));
  };

  const handleNextDay = () => {
    const newDate = addDays(selectedDate, 1);
    setSelectedDate(newDate);
    localStorage.setItem(LAST_SELECTED_DATE_KEY, format(newDate, 'yyyy-MM-dd'));
  };

  const handleDateChange = (newDate: Date | null) => {
    if (newDate) {
      // Use local calendar date only (midnight local) so display and API string match
      // setUTCHours(0,0,0,0) would shift to previous day in timezones behind UTC
      const normalizedDate = new Date(newDate.getFullYear(), newDate.getMonth(), newDate.getDate());
      setSelectedDate(normalizedDate);
      localStorage.setItem(LAST_SELECTED_DATE_KEY, format(normalizedDate, 'yyyy-MM-dd'));
    }
  };

  // Filter tasks based on search
  useEffect(() => {
    const filtered = allTasks.filter(
      (task) =>
        task.name.toLowerCase().includes(searchTask.toLowerCase()) ||
        (task.description || '').toLowerCase().includes(searchTask.toLowerCase()),
    );
    setFilteredTasks(filtered);
  }, [searchTask, allTasks]);

  // Filter people based on name and skill search
  useEffect(() => {
    let filtered = people.filter(
      (person) =>
        (searchName === '' || person.name.toLowerCase().includes(searchName.toLowerCase())) &&
        (searchSkill === '' ||
          (person.ranking || '').toLowerCase().includes(searchSkill.toLowerCase()) ||
          (person.varos &&
            person.varos.some(
              (pv) =>
                (pv.varos?.name || '').toLowerCase().includes(searchSkill.toLowerCase()) ||
                (pv.varos?.description || '').toLowerCase().includes(searchSkill.toLowerCase()),
            ))),
    );
    // Sort by last name
    filtered = filtered.sort((a, b) => {
      const aLast = a.name.trim().split(' ').slice(-1)[0].toLowerCase();
      const bLast = b.name.trim().split(' ').slice(-1)[0].toLowerCase();
      if (aLast < bLast) return -1;
      if (aLast > bLast) return 1;
      return a.name.localeCompare(b.name);
    });
    setFilteredPeople(filtered);
  }, [searchName, searchSkill, people]);

  const handlePersonSelect = (personId: number) => {
    console.log('Selecting person:', personId);
    setSelectedPerson(personId);
    console.log('Updated selection:', {
      personId,
      taskId: selectedTaskId,
      isEditing: !!editingAssignment,
    });
  };

  const handleTaskSelect = (taskId: number, taskName: string) => {
    setSelectedTaskId(taskId);
    setSelectedTask(taskName);
  };

  const handleOpenNewPersonDialog = () => {
    setOpenNewPersonDialog(true);
    setNewFirstName('');
    setNewLastName('');
    setNewEmail('');
    setNewPhone('');
    setNewRanking('');
    setNewNotes('');
    setSelectedSkills([]);
  };

  const handleCloseNewPersonDialog = () => {
    setOpenNewPersonDialog(false);
    setNewFirstName('');
    setNewLastName('');
    setNewEmail('');
    setNewPhone('');
    setNewRanking('');
    setNewNotes('');
    setSelectedSkills([]);
  };

  const handleCreatePerson = async () => {
    if (!newFirstName.trim() || !newLastName.trim()) return;
    try {
      const response = await axios.post(
        `${config.api.baseUrl}/people`,
        {
          name: `${newFirstName.trim()} ${newLastName.trim()}`,
          email: newEmail || null,
          phone: newPhone || null,
          ranking: newRanking || null,
          notes: newNotes || null,
          skills: selectedSkills,
        },
        {
          withCredentials: true,
        }
      );
      const newPerson = response.data;
      setPeople((prevPeople) => [...prevPeople, newPerson]);
      setFilteredPeople((prevFiltered) => [...prevFiltered, newPerson]);
      setSelectedPerson(newPerson.id);
      handleCloseNewPersonDialog();
    } catch (error) {
      console.error('Error creating person:', error);
    }
  };

  const handleSkillChange = (event: SelectChangeEvent<number[]>) => {
    setSelectedSkills(event.target.value as number[]);
  };

  const handleExportToExcel = () => {
    // Prepare data for standard assignments (template varos)
    const standardAssignmentData = standardVarosTemplate.map((taskName) => {
      const assignment = assignments.find(
        (a) =>
          a.varos.name === taskName &&
          format(new Date(a.startDate), 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd'),
      );
      const person = assignment ? people.find((p) => p.id === assignment.personId) : undefined;

      return {
        Date: format(selectedDate, 'yyyy-MM-dd'),
        'Varo Type': 'Standard',
        Special: specialForDate?.name ?? '',
        'Varo Name': taskName,
        'Assigned To': person?.name || 'Unassigned',
        'Phone Number': person?.phone || '',
        Email: person?.email || '',
      };
    });

    // Prepare data for other assignments (Special column from Special table for this date)
    const otherAssignmentData = assignments
      .filter((assignment) => !standardVarosTemplate.includes(assignment.varos.name))
      .map((assignment) => {
        const person = people.find((p) => p.id === assignment.personId);
        return {
          Date: format(new Date(assignment.startDate), 'yyyy-MM-dd'),
          'Varo Type': 'Other',
          Special: specialForDate?.name ?? '',
          'Varo Name': assignment.varos.name,
          'Assigned To': person?.name || 'Unassigned',
          'Phone Number': person?.phone || '',
          Email: person?.email || '',
        };
      });

    // Combine all data
    const allData = [...standardAssignmentData, ...otherAssignmentData];

    // Create worksheet
    const ws = XLSX.utils.json_to_sheet(allData);

    // Auto-fit columns
    const colWidths: number[] = [];
    const maxWidth = 50; // Maximum column width
    const padding = 2; // Padding for better readability

    // Get headers (first row keys)
    if (allData.length > 0) {
      const headers = Object.keys(allData[0]);
      headers.forEach((header, colIndex) => {
        colWidths[colIndex] = header.length + padding;
      });
    }

    // Calculate maximum width for each column based on content
    allData.forEach((row) => {
      Object.values(row).forEach((value, colIndex) => {
        const cellValue = String(value || '');
        colWidths[colIndex] = Math.min(
          maxWidth,
          Math.max(colWidths[colIndex], cellValue.length + padding),
        );
      });
    });

    // Apply column widths
    ws['!cols'] = colWidths.map((width) => ({ width }));

    // Create workbook
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Assignments');

    // Generate filename with date
    const fileName = `assignments_${format(selectedDate, 'yyyy-MM-dd')}.xlsx`;

    // Save file
    XLSX.writeFile(wb, fileName);
  };

  const handleDeleteClick = (assignment: Assignment) => {
    setAssignmentToDelete(assignment);
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!assignmentToDelete) return;

    try {
      console.log('Deleting assignment:', assignmentToDelete.id);
      const response = await axios.delete(`${config.api.baseUrl}/assignments/${assignmentToDelete.id}`, {
        withCredentials: true,
      });
      console.log('Delete response:', response.data);
      
      // Close confirmation dialog
      setDeleteConfirmOpen(false);
      setAssignmentToDelete(null);
      
      // Refresh assignments after delete
      await fetchAssignments();
    } catch (error: any) {
      console.error('Error deleting assignment:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        assignmentId: assignmentToDelete.id,
      });
    }
  };

  const handleDeleteCancel = () => {
    setDeleteConfirmOpen(false);
    setAssignmentToDelete(null);
  };

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', p: { xs: 1, sm: 3 } }}>
      {isMobile ? (
        <>
          <Typography variant="h4" sx={{ mr: 3 }} className="print-hide-header">
            Assignments
          </Typography>
          <Box sx={{ mt: 1, mb: 2, width: '100%', display: 'flex', alignItems: 'center' }}>
            <IconButton onClick={handlePreviousDay} size="small">
              <ChevronLeftIcon />
            </IconButton>
            <DatePicker
              label="Select Date"
              value={selectedDate}
              onChange={handleDateChange}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                },
              }}
              sx={{ width: '100%', mx: 1 }}
            />
            <IconButton onClick={handleNextDay} size="small">
              <ChevronRightIcon />
            </IconButton>
          </Box>
        </>
      ) : (
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
          <Typography variant="h4" sx={{ mr: 3 }} className="print-hide-header">
            Assignments
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
            <IconButton onClick={handlePreviousDay} size="small">
              <ChevronLeftIcon />
            </IconButton>
            <DatePicker
              label="Select Date"
              value={selectedDate}
              onChange={handleDateChange}
              slotProps={{
                textField: {
                  size: 'small',
                  fullWidth: true,
                },
              }}
              sx={{ width: { xs: '100%', sm: 220 }, mx: 1 }}
            />
            <IconButton onClick={handleNextDay} size="small">
              <ChevronRightIcon />
            </IconButton>
            <Box sx={{
              display: 'flex',
              flexDirection: { xs: 'column', sm: 'row' },
              alignItems: { xs: 'stretch', sm: 'center' },
              justifyContent: { xs: 'flex-start', sm: 'flex-end' },
              width: '100%',
              gap: { xs: 1, sm: 2 },
              mt: { xs: 2, sm: 0 },
            }}>
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                onClick={handleExportToExcel}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Export to Excel
              </Button>
              <Button
                variant="outlined"
                onClick={() => window.print()}
                sx={{ width: { xs: '100%', sm: 'auto' } }}
              >
                Print
              </Button>
            </Box>
          </Box>
        </Box>
      )}

      <style>{`
@media print {
  body * {
    visibility: hidden;
  }
  #assignments-print-root, #assignments-print-root * {
    visibility: visible;
  }
  #assignments-print-root {
    position: absolute;
    left: 0;
    top: 0;
    width: 100vw;
    background: white;
    color: black;
    box-shadow: none;
  }
  .print-person-name,
  .print-person-name *,
  .print-person-name-card,
  .print-person-name-card * {
    text-align: left !important;
    justify-content: flex-start !important;
    align-items: flex-start !important;
  }
  .print-hide-header { display: none !important; }
  .print-hide-bk-satada { display: none !important; }
  .print-hide-empty-other { display: none !important; }
  .MuiButton-root, .MuiIconButton-root, .MuiPagination-root, nav, header, footer {
    display: none !important;
  }
  .print-standard-table,
  .print-other-table {
    width: 500px !important;
    min-width: 500px !important;
    max-width: 500px !important;
    margin-left: 0 !important;
    margin-right: 0 !important;
  }
  .print-standard-table-container,
  .print-other-table-container {
    display: block !important;
  }
  .screen-standard-cards,
  .screen-other-table-container {
    display: none !important;
  }
  .print-standard-table td:first-child,
  .print-standard-table th:first-child,
  .print-other-table td:first-child,
  .print-other-table th:first-child {
    width: 200px;
    min-width: 200px;
    max-width: 200px;
    padding-left: 0 !important;
    text-align: left !important;
    white-space: nowrap !important;
    word-break: normal !important;
  }
  .print-standard-table td:nth-child(2),
  .print-standard-table th:nth-child(2),
  .print-other-table td:nth-child(2),
  .print-other-table th:nth-child(2) {
    width: 180px;
    min-width: 180px;
    max-width: 180px;
    text-align: left !important;
    padding-left: 6em !important;
  }
  .print-section-title {
    font-size: 1rem !important;
  }
  .print-header-space {
    margin-bottom: 1.5em !important;
    display: block !important;
  }
}
@media screen {
  .print-standard-table,
  .print-standard-table-container,
  .print-other-table,
  .print-other-table-container {
    display: none !important;
  }
}
`}</style>

      <div id="assignments-print-root">
        <div className="print-header-space"></div>
        <>
          {/* Daily Standard Assignments Section */}
          <Box sx={{ mb: 4 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', fontSize: { xs: '1.1rem', sm: '1.5rem' } }}
                className="print-section-title"
              >
                {(() => {
                  if (specialForDate?.name) {
                    return specialForDate.name;
                  }
                  const dayName = format(selectedDate, 'EEEE');
                  if (dayName === 'Monday') return 'Monday Ceremonies';
                  if (dayName === 'Friday') return 'Friday Ceremonies';
                  return 'Standard Varos';
                })()}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary" className="print-hide-header">
                {format(selectedDate, 'EEEE MMMM d, yyyy')}
              </Typography>
              <Button
                variant="outlined"
                size="small"
                sx={{ ml: 2 }}
                onClick={() => {
                  setSpecialNameInput(specialForDate?.name || '');
                  setSpecialDialogOpen(true);
                }}
              >
                Special
              </Button>
            </Box>

            {standardVarosTemplate.length > 0 && (
            <div className="screen-standard-cards">
              <Stack spacing={2}>
                {standardVarosTemplate.map((taskName) => {
                  const assignment = assignments.find(
                    (a) =>
                      a.varos.name === taskName &&
                      format(new Date(a.startDate), 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd'),
                  );
                  const person = assignment
                    ? people.find((p) => p.id === assignment.personId)
                    : undefined;

                  return (
                    <Card
                      key={taskName}
                      sx={{
                        width: '100%',
                        '&:hover': {
                          boxShadow: 3,
                        },
                      }}
                    >
                      <CardContent
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.15rem' } }}>{taskName}</Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {person ? (
                            <>
                              <Box sx={{ display: 'flex', flexDirection: 'column' }} className="print-person-name-card">
                                <Typography variant="body1" className="print-person-name" sx={{ fontSize: { xs: '1.0rem', sm: '1rem' } }}>{person.name}</Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                                  {person.phone && (
                                    <Typography variant="caption" color="text.secondary">
                                      {person.phone}
                                    </Typography>
                                  )}
                                  {person.email && (
                                    <Typography variant="caption" color="text.secondary">
                                      {person.email}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="Edit Assignment">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenDialog(taskName, assignment)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                {assignment && (
                                  <Tooltip title="Delete Assignment">
                                    <IconButton
                                      size="small"
                                      onClick={() => handleDeleteClick(assignment)}
                                      color="default"
                                      sx={{ color: 'action.disabled' }}
                                    >
                                      <DeleteIcon />
                                    </IconButton>
                                  </Tooltip>
                                )}
                              </Box>
                            </>
                          ) : (
                            <Button
                              variant="outlined"
                              startIcon={<PersonAddIcon />}
                              onClick={() => handleOpenDialog(taskName)}
                              size="small"
                            >
                              Assign Murid
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </div>
            )}

            {standardVarosTemplate.length > 0 && (
            <div className="print-standard-table-container">
              <Table className="print-standard-table">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>Varo Name</TableCell>
                    <TableCell sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>Assigned To</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {standardVarosTemplate.map((taskName) => {
                    const assignment = assignments.find(
                      (a) =>
                        a.varos.name === taskName &&
                        format(new Date(a.startDate), 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd'),
                    );
                    const person = assignment ? people.find((p) => p.id === assignment.personId) : undefined;
                    return (
                      <TableRow key={taskName}>
                        <TableCell sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>{taskName}</TableCell>
                        <TableCell sx={{ pl: { xs: 2, sm: 0 } }}>{person?.name || ''}</TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
            )}
          </Box>

          {/* Other assignments: manual list; hide "Other Varos" title when custom special day */}
          <Box
            sx={{ mt: 6 }}
            className={nonStandardAssignments.length === 0 ? 'print-hide-empty-other' : ''}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: isCustomSpecialDay ? 'flex-end' : 'space-between',
                alignItems: 'center',
                mb: 3,
              }}
            >
              {!isCustomSpecialDay && (
                <Typography
                  variant="h5"
                  className="print-section-title"
                  sx={{ fontSize: { xs: '1.1rem', sm: '1.5rem' } }}
                >
                  Other Varos
                </Typography>
              )}
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('')}>
                Create Varo Assignment
              </Button>
            </Box>
            <div className="screen-other-table-container">
              <Stack spacing={2}>
                {nonStandardAssignments.map((assignment) => {
                  const person = people.find((p) => p.id === assignment.personId);
                  return (
                    <Card
                      key={assignment.id}
                      sx={{
                        width: '100%',
                        '&:hover': {
                          boxShadow: 3,
                        },
                      }}
                    >
                      <CardContent
                        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
                      >
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="h6" sx={{ fontSize: { xs: '1.1rem', sm: '1.15rem' } }}>
                            {assignment.varos.name}
                          </Typography>
                        </Box>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          {person ? (
                            <>
                              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                                <Typography
                                  variant="body1"
                                  sx={{ fontSize: { xs: '1.0rem', sm: '1rem' } }}
                                >
                                  {person.name}
                                </Typography>
                                <Box sx={{ display: 'flex', flexDirection: 'column', mt: 0.5 }}>
                                  {person.phone && (
                                    <Typography variant="caption" color="text.secondary">
                                      {person.phone}
                                    </Typography>
                                  )}
                                  {person.email && (
                                    <Typography variant="caption" color="text.secondary">
                                      {person.email}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              <Box sx={{ display: 'flex', gap: 1 }}>
                                <Tooltip title="Edit Assignment">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleOpenDialog(assignment.varos.name, assignment)}
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Tooltip>
                                <Tooltip title="Delete Assignment">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleDeleteClick(assignment)}
                                    color="default"
                                    sx={{ color: 'action.disabled' }}
                                  >
                                    <DeleteIcon />
                                  </IconButton>
                                </Tooltip>
                              </Box>
                            </>
                          ) : (
                            <Button
                              variant="outlined"
                              startIcon={<PersonAddIcon />}
                              onClick={() => handleOpenDialog(assignment.varos.name)}
                              size="small"
                            >
                              Assign Murid
                            </Button>
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  );
                })}
              </Stack>
            </div>
            {nonStandardAssignments.length > 0 && (
              <div className="print-other-table-container">
                <Table className="print-other-table">
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>Varo</TableCell>
                      <TableCell sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>Assigned To</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {nonStandardAssignments.map((assignment) => {
                      const person = people.find((p) => p.id === assignment.personId);
                      return (
                        <TableRow key={assignment.id}>
                          <TableCell sx={{ fontSize: { xs: '0.95rem', sm: '1rem' } }}>{assignment.varos.name}</TableCell>
                          <TableCell sx={{ pl: { xs: 2, sm: 0 } }}>{person?.name || 'Unassigned'}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </Box>
        </>
      </div>

      {/* Special name dialog for selected date */}
      <Dialog
        open={specialDialogOpen}
        onClose={() => setSpecialDialogOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Set Special Day Name</DialogTitle>
        <DialogContent>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Date: {format(selectedDate, 'EEEE MMMM d, yyyy')}
          </Typography>
          <TextField
            label="Special Name"
            value={specialNameInput}
            onChange={(e) => setSpecialNameInput(e.target.value)}
            fullWidth
            margin="normal"
            autoFocus
          />
        </DialogContent>
        <DialogActions>
          {specialForDate && (
            <Button
              color="error"
              onClick={handleRemoveSpecialForDate}
              sx={{ mr: 'auto' }}
            >
              Remove
            </Button>
          )}
          <Button onClick={() => setSpecialDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleSaveSpecialForDate}
            disabled={!specialNameInput.trim()}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>

      {/* Assignment Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{editingAssignment ? 'Edit Assignment' : 'New Assignment'}</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <FormControl fullWidth margin="normal">
              <InputLabel id="task-label">Varo</InputLabel>
              <Select
                labelId="task-label"
                value={selectedTask}
                onChange={handleTaskChange}
                label="Varo"
              >
                {standardVarosTemplate.map((task) => (
                  <MenuItem key={task} value={task}>
                    {task}
                  </MenuItem>
                ))}
                {allTasks
                  .filter((task) => !standardVarosTemplate.includes(task.name))
                  .map((task) => (
                    <MenuItem key={task.id} value={task.name}>
                      {task.name}
                      {task.description && (
                        <Typography variant="caption" color="textSecondary" sx={{ ml: 1 }}>
                          ({task.description})
                        </Typography>
                      )}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <Typography variant="subtitle1" sx={{ mt: 2 }} gutterBottom>
              Murid
            </Typography>
            <Stack spacing={2}>
              <TextField
                fullWidth
                placeholder="Search by name..."
                value={searchName}
                onChange={(e) => setSearchName(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                  endAdornment: searchName ? (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchName('')}
                        aria-label="Clear name"
                        sx={{ p: 0.5 }}
                      >
                        <CloseIcon fontSize="small" sx={{ fontSize: 18 }} />
                      </IconButton>
                    </InputAdornment>
                  ) : null,
                }}
              />
              <TextField
                fullWidth
                placeholder="Search by skill..."
                value={searchSkill}
                onChange={(e) => setSearchSkill(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
            </Stack>
            <List sx={{ mt: 1, maxHeight: 200, overflow: 'auto' }}>
              {filteredPeople.length === 0 && searchName === '' && searchSkill === '' ? (
                <ListItem>
                  <ListItemText
                    primary="Enter search criteria to find people"
                    secondary="Use the search boxes above to filter by name or skills"
                  />
                </ListItem>
              ) : filteredPeople.length === 0 ? (
                <ListItem>
                  <ListItemText
                    primary="No matches found"
                    secondary="Try adjusting your search criteria"
                  />
                </ListItem>
              ) : (
                filteredPeople.map((person) => (
                  <ListItem
                    key={person.id}
                    component="div"
                    onClick={() => handlePersonSelect(person.id)}
                    sx={{
                      cursor: 'pointer',
                      bgcolor: selectedPerson === person.id ? 'action.selected' : 'transparent',
                    }}
                  >
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography>{
                            (() => {
                              const parts = person.name.trim().split(' ');
                              if (parts.length === 1) return parts[0];
                              const last = parts.pop();
                              return `${last}, ${parts.join(' ')}`;
                            })()
                          }</Typography>
                          {person.ranking && (
                            <Chip label={person.ranking} size="small" color="secondary" />
                          )}
                        </Box>
                      }
                      secondary={
                        <Stack direction="column" spacing={0.5}>
                          <Box>{person.varos?.map((pv) => pv.varos?.name).join(', ')}</Box>
                          {person.assignments && person.assignments.length > 0 && (
                            <>
                              {(() => {
                                const { lastAssignment, nextAssignment } = getAssignments(
                                  person.assignments,
                                  selectedDate,
                                );
                                return (
                                  <>
                                    {lastAssignment && (
                                      <Typography variant="body1" sx={{ mt: 2 }}>
                                        Last Assignment {getRelativeDays(lastAssignment.startDate, selectedDate)}:
                                        {lastAssignment.varos.name}{lastAssignment.person && ` - ${lastAssignment.person.name}`}
                                      </Typography>
                                    )}
                                    {nextAssignment && (
                                      <Typography variant="body1">
                                        Upcoming Assignment {getRelativeDays(nextAssignment.startDate, selectedDate)}:
                                        {nextAssignment.varos.name}{nextAssignment.person && ` - ${nextAssignment.person.name}`}
                                      </Typography>
                                    )}
                                  </>
                                );
                              })()}
                            </>
                          )}
                        </Stack>
                      }
                    />
                  </ListItem>
                ))
              )}
            </List>
          </Box>
        </DialogContent>
        <DialogActions sx={{ display: 'flex', justifyContent: 'space-between', px: 2, py: 1.5 }}>
          <Button startIcon={<AddIcon />} onClick={handleOpenNewPersonDialog}>
            Add New Murid
          </Button>
          <Box>
            <Button onClick={handleCloseDialog}>Cancel</Button>
            <Button
              onClick={handleAssign}
              variant="contained"
              color="primary"
              disabled={!selectedPerson || !selectedTaskId}
            >
              {editingAssignment ? 'Save Changes' : 'Create Assignment'}
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* New Person Dialog */}
      <Dialog
        open={openNewPersonDialog}
        onClose={handleCloseNewPersonDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Add New Murid</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>
              Name
            </Typography>
            <Stack direction="row" spacing={2}>
              <TextField
                autoFocus
                margin="normal"
                placeholder="First"
                value={newFirstName}
                onChange={e => setNewFirstName(e.target.value)}
                required
                fullWidth
                size="small"
              />
              <TextField
                margin="normal"
                placeholder="Last"
                value={newLastName}
                onChange={e => setNewLastName(e.target.value)}
                required
                fullWidth
                size="small"
              />
            </Stack>
            <TextField
              fullWidth
              label="Email"
              type="email"
              value={newEmail}
              onChange={(e) => setNewEmail(e.target.value)}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Phone"
              value={newPhone}
              onChange={(e) => setNewPhone(e.target.value)}
              margin="normal"
            />
            <FormControl fullWidth margin="normal">
              <InputLabel id="skills-label">Skills</InputLabel>
              <Select
                labelId="skills-label"
                label="Skills"
                multiple
                value={selectedSkills}
                onChange={handleSkillChange}
                renderValue={(selected) => (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {selected.map((value) => (
                      <Chip
                        key={value}
                        label={allTasks.find((t) => t.id === value)?.name}
                        size="small"
                      />
                    ))}
                  </Box>
                )}
              >
                {allTasks.map((task) => (
                  <MenuItem key={task.id} value={task.id}>
                    {task.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              fullWidth
              label="Ranking"
              value={newRanking}
              onChange={(e) => setNewRanking(e.target.value)}
              margin="normal"
            />
            <TextField
              fullWidth
              label="Notes"
              value={newNotes}
              onChange={(e) => setNewNotes(e.target.value)}
              margin="normal"
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseNewPersonDialog}>Cancel</Button>
          <Button
            onClick={handleCreatePerson}
            variant="contained"
            color="primary"
            disabled={!newFirstName.trim() || !newLastName.trim()}
          >
            Create Murid
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteConfirmOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Delete Assignment</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this assignment?
            {assignmentToDelete && (
              <Box component="span" sx={{ display: 'block', mt: 1, color: 'text.secondary' }}>
                {assignmentToDelete.varos.name} - {people.find((p) => p.id === assignmentToDelete.personId)?.name}
              </Box>
            )}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AssignmentManager;
